// Breakpoint reattivi via window.matchMedia, allineati alla scala Tailwind
// (sm 640 / md 768 / lg 1024). Sostituisce il rilevamento "mobile" via
// user-agent (isMobile in globalStore.js / app_const.js): il layout dipende
// dalla larghezza del viewport, non dal device — un tablet in landscape o una
// finestra desktop stretta devono comportarsi come ciò che mostrano.
//
// Uso (Options API o setup):
//   import { useViewport } from '@/composables/useViewport'
//   const viewport = useViewport()
//   viewport.mdUp.value  // true da 768px in su (in template: viewport.mdUp)
//
// Stato singleton a livello di modulo: i listener matchMedia vivono per tutta
// la pagina (nessun teardown per-componente necessario).
import { reactive, computed } from 'vue'

// Scala Tailwind (tailwind.config.js non ridefinisce theme.screens).
export const BREAKPOINTS = { sm: 640, md: 768, lg: 1024 }

const state = reactive({ sm: false, md: false, lg: false })
let initialized = false
let teardowns = []

function init() {
  if (initialized) return
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return
  initialized = true
  for (const [key, px] of Object.entries(BREAKPOINTS)) {
    const mql = window.matchMedia(`(min-width: ${px}px)`)
    state[key] = mql.matches
    const onChange = (e) => { state[key] = e.matches }
    // Safari < 14 espone solo addListener/removeListener.
    if (typeof mql.addEventListener === 'function') {
      mql.addEventListener('change', onChange)
      teardowns.push(() => mql.removeEventListener('change', onChange))
    } else if (typeof mql.addListener === 'function') {
      mql.addListener(onChange)
      teardowns.push(() => mql.removeListener(onChange))
    }
  }
}

const smUp = computed(() => state.sm)
const mdUp = computed(() => state.md)
const lgUp = computed(() => state.lg)
// Fasce derivate: "mobile" = telefono (<768), "tablet" = 768–1023,
// "desktop" = da 1024 (soglia a cui l'header torna alla nav estesa).
const isMobile = computed(() => !state.md)
const isTablet = computed(() => state.md && !state.lg)
const isDesktop = computed(() => state.lg)

export function useViewport() {
  init()
  return { smUp, mdUp, lgUp, isMobile, isTablet, isDesktop }
}

// Solo per i test: smonta i listener e ripristina lo stato iniziale, così ogni
// spec può stubbare window.matchMedia da zero (stesso pattern dei mock
// __resetFirebase / __resetAxios).
export function __resetViewportForTests() {
  teardowns.forEach((fn) => fn())
  teardowns = []
  initialized = false
  state.sm = false
  state.md = false
  state.lg = false
}
